import React, { Dispatch, SetStateAction, useState } from "react";

import DataTable from "components/basic/data-table/DataTable";
import { InterruptedService, MaintenanceEmail, MeasurementTypeDef } from "gql/generated/types";
import { Maybe } from "graphql/jsutils/Maybe";
import { Accordion, AccordionTab } from "primereact/accordion";
import { Button } from "primereact/button";
import { confirmPopup } from "primereact/confirmpopup";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { T } from "services/lang/LanguageService";
import { v4 } from "uuid";
import ActionButtons from "components/basic/action-buttons/ActionButtons";
import { TableColumn } from "../../utils/types";
import { renderTag } from "../../utils/utils";
import MaintenanceEmailForm from "./MaintenanceEmailForm";

type MaintenanceConfigurationProps = {
  emails: MaintenanceEmail[];
  setEmails: Dispatch<SetStateAction<MaintenanceEmail[]>>;
  interruptedServices: InterruptedService[];
  setInterruptedServices: Dispatch<SetStateAction<InterruptedService[]>>;
  deviceRoles: MeasurementTypeDef[]
};

const MaintenanceConfiguration: React.FC<MaintenanceConfigurationProps> = ({
  emails,
  setEmails,
  interruptedServices,
  setInterruptedServices,
  deviceRoles,
}) => {
  const [dialogOpen, setDialogOpen] = useState({ emailForm: false, recipients: false });
  const [recipients, setRecipients] = useState<{ to: Maybe<string>[]; cc: Maybe<string>[] }>({
    to: [],
    cc: [],
  });
  const [emailId, setEmailId] = useState<string | null>(null);

  const renderRecipients = (email: MaintenanceEmail) => {
    const totalRecipients = email.recipientsTo.length + email.recipientsCc.length;
    return (
      <Button
        label={totalRecipients.toString()}
        icon="pi pi-users"
        text
        iconPos="right"
        size="small"
        className="w-min"
        onClick={() => {
          setDialogOpen({ emailForm: false, recipients: true });
          setRecipients({ to: email.recipientsTo, cc: email.recipientsCc });
        }}
      />
    );
  };

  const renderActions = (email: MaintenanceEmail) => (
    <ActionButtons
      onClickDelete={() => setEmails(prev => prev.filter(e => e.id !== email.id))}
      onClickEdit={() => {
        setDialogOpen({ emailForm: true, recipients: false });
        setEmailId(email.id);
      }}
    />
  );

  const emailsColumns: TableColumn<MaintenanceEmail>[] = [
    {
      field: "title",
      header: T("word.title"),
      columnKey: "title",
    },
    {
      field: "header",
      header: T("word.header"),
      columnKey: "header",
    },
    {
      field: "triggerFrom",
      header: T("mdm.from-status"),
      body: rowData => renderTag({ status: rowData.triggerFrom }),
      columnKey: "from",
    },
    {
      field: "triggerTo",
      header: T("mdm.to-status"),
      body: rowData => renderTag({ status: rowData.triggerTo }),
      columnKey: "to",
    },
    {
      field: "recipientsTo",
      header: T("word.recipients"),
      body: renderRecipients,
      columnKey: "recipientsTo",
      className: "w-1",
    },
    {
      header: T("word.actions"),
      columnKey: "action",
      body: renderActions,
      className: "w-1",
    },
  ];

  const handleCloseDialog = () => {
    setDialogOpen({ emailForm: false, recipients: false });
    setEmailId(null);
  };

  return (
    <Accordion activeIndex={0}>
      <AccordionTab header={T("word.email-templates")}>
        <DataTable
          columns={emailsColumns}
          data={emails}
          tableConfig={{
            header: (
              <Button
                type="button"
                icon="pi pi-plus"
                severity="success"
                outlined
                rounded
                onClick={() => setDialogOpen({ emailForm: true, recipients: false })}
              />
            ),
          }}
        />
        <Dialog
          header={
            emailId
              ? `${T("word.template")} (${emails.find(e => e.id === emailId)?.title ?? "-"})`
              : T("word.template")
          }
          style={{ width: "min(700px, 100%)"}}
          visible={dialogOpen.emailForm}
          contentClassName="p-0 pb-4 md:p-4"
          onHide={handleCloseDialog}
        >
          <MaintenanceEmailForm
            setEmails={setEmails}
            email={emails.find(e => e.id === emailId)}
            closeDialog={handleCloseDialog}
          />
        </Dialog>
        <Dialog
          header={T("word.recipients")}
          visible={dialogOpen.recipients}
          onHide={handleCloseDialog}
        >
          <Recipients recipients={recipients} />
        </Dialog>
      </AccordionTab>
      <AccordionTab header={T("word.interrupted-services")}>
        <div className="flex flex-wrap gap-3">
          {interruptedServices.map(is => (
            <InterruptedServiceItem
              key={is.id}
              service={is}
              setServices={setInterruptedServices}
              deviceRoles={deviceRoles}
            />
          ))}
        </div>
        <Button
          icon="pi pi-plus"
          className="p-button-rounded p-button-success p-button-outlined p-button-sm mt-3"
          title={T("action.add-new-type")}
          onClick={() => {
            if (interruptedServices.some(is => !is.title)) return;
            setInterruptedServices(prev => [...prev, { id: v4(), title: "", roleId: "" }]);
          }}
        />
      </AccordionTab>
    </Accordion>
  );
};

export default MaintenanceConfiguration;

type InterruptedServiceItemProps = {
  service: InterruptedService;
  setServices: Dispatch<SetStateAction<InterruptedService[]>>;
  deviceRoles: MeasurementTypeDef[]
};
const InterruptedServiceItem: React.FC<InterruptedServiceItemProps> = ({
  service,
  setServices,
  deviceRoles,
}) => {
  const confirmDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
    confirmPopup({
      target: e.currentTarget,
      message: T("confirm-dialog.are-you-sure-you-want-to-delete"),
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () => {
        setServices(prev => prev.filter(s => s.id !== service.id));
      },
    });
  };
  
  return (
    <div className="p-inputgroup w-30rem mt-2">
      <span className="p-float-label">
        <InputText
          value={service.title}
          onChange={e =>
            setServices(prev =>
              prev.map(s => (s.id === service.id ? { ...s, title: e.target.value } : s))
            )
          }
          className="p-inputtext-sm"
          title={T("word.role-title")}
          id="title"
          placeholder={`${T("action.write-title")}...`}
        />
        <label htmlFor="title" className="text-sm">
          {T("word.title")}
        </label>
      </span>
      <span className="p-float-label">
        <Dropdown
          value={service.roleId}
          options={deviceRoles}
          optionLabel="title"
          optionValue="id"
          onChange={e =>
            setServices(prev =>
              prev.map(s => (s.id === service.id ? { ...s, roleId: e.value } : s))
            )
          }
          id="roleId"
          className="p-inputtext-sm"
        />
        <label htmlFor="roleId" className="text-sm">
          {T("word.device-role")}
        </label>
      </span>
      <Button
        type="button"
        icon="pi pi-times"
        className="p-inputgroup-addon"
        severity="danger"
        outlined
        onClick={confirmDelete}
      />
    </div>
  );
};

const Recipients: React.FC<{ recipients: { to: Maybe<string>[]; cc: Maybe<string>[] } }> = ({
  recipients,
}) => {
  return (
    <div className="m-3" style={{ maxHeight: "500px" }}>
      <h6>[To]:</h6>
      <div className="w-min line-height-3">{recipients.to.join(", ")}</div>
      {recipients.cc.length > 0 && (
        <>
          <h6>[Cc]:</h6>
          <div className="w-min line-height-3">{recipients.cc.join(", ")}</div>
        </>
      )}
    </div>
  );
};
